import React, {
  InputHTMLAttributes,
  forwardRef,
  useCallback,
  useEffect,
  useState,
} from "react";

interface FileUploaderProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "onChange"> {
  file?: File | null;
  existingUrl?: string;
  onChange?: (file: File | null) => void;
}

const boxStyle: React.CSSProperties = {
  border: "1px solid #eee",
  borderRadius: 12,
  backgroundColor: "#fcfcfc",
  padding: "10px 12px",
};

const FileUploaderV2 = forwardRef<HTMLInputElement, FileUploaderProps>(
  function FileUploader(props, ref) {
    const { file, existingUrl, onChange, ...inputProps } = props;

    const [editMode, setEditMode] = useState(false);
    const [fileUrl, setFileUrl] = useState<string | null>(null);

    useEffect(() => {
      if (file instanceof File) {
        const url = URL.createObjectURL(file);
        setFileUrl(url);
        return () => URL.revokeObjectURL(url);
      }
      setFileUrl(null);
    }, [file]);

    const displayUrl = fileUrl || existingUrl || null;
    const isNew = file instanceof File;

    const handleFileChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFile = e.target.files?.[0];
        if (selectedFile && onChange) {
          onChange(selectedFile);
          setEditMode(false);
        }
        // allow picking the same file again later
        e.target.value = "";
      },
      [onChange],
    );

    const picker = (label: string) => (
      <label
        className="btn btn-sm btn-outline-primary rounded-pill px-3 mb-0"
        style={{ cursor: "pointer" }}
      >
        {label}
        <input
          type="file"
          ref={ref}
          {...inputProps}
          onChange={handleFileChange}
          style={{ display: "none" }}
        />
      </label>
    );

    return (
      <div style={boxStyle}>
        {displayUrl && !editMode ? (
          <div className="d-flex align-items-center justify-content-between gap-3">
            <div
              className="d-flex align-items-center gap-3"
              style={{ minWidth: 0 }}
            >
              <img
                src={displayUrl}
                alt="Aperçu"
                width={44}
                height={44}
                className="flex-shrink-0"
                style={{ objectFit: "cover", borderRadius: 10 }}
              />
              <div style={{ minWidth: 0 }}>
                <a
                  href={displayUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="fw-semibold text-dark text-truncate d-block"
                  style={{ fontSize: 13 }}
                >
                  {isNew ? file!.name : "Image téléchargée"}
                </a>
                <span
                  className="d-inline-flex align-items-center rounded-pill mt-1"
                  style={{
                    height: 20,
                    padding: "0 8px",
                    fontSize: 11,
                    fontWeight: 700,
                    color: isNew ? "#f0813f" : "#198754",
                    backgroundColor: isNew
                      ? "rgba(240,129,63,0.14)"
                      : "rgba(25,135,84,0.12)",
                  }}
                >
                  {isNew ? "Nouveau fichier" : "✓ Enregistrée"}
                </span>
              </div>
            </div>

            <button
              type="button"
              className="btn btn-sm btn-outline-secondary rounded-pill px-3 flex-shrink-0"
              onClick={() => setEditMode(true)}
            >
              Modifier
            </button>
          </div>
        ) : (
          <div className="d-flex align-items-center justify-content-between gap-3">
            <div className="d-flex align-items-center gap-3">
              {picker(displayUrl ? "Choisir un fichier" : "Choisir une image")}
              <span className="text-muted" style={{ fontSize: 12 }}>
                {inputProps.accept ? "PNG ou JPG" : "Aucun fichier sélectionné"}
              </span>
            </div>

            {editMode && (
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary rounded-pill px-3"
                onClick={() => setEditMode(false)}
              >
                Annuler
              </button>
            )}
          </div>
        )}
      </div>
    );
  },
);

export default FileUploaderV2;
